import { IconButton } from '@atlaskit/button/new';
import { type NewCoreIconProps } from '@atlaskit/icon';
import DragHandleHorizontalIcon from '@atlaskit/icon/core/drag-handle-horizontal';
import DragHandleVerticalIcon from '@atlaskit/icon/core/drag-handle-vertical';
import EyeOpenStrikethroughIcon from '@atlaskit/icon/core/eye-open-strikethrough';
import FieldIcon from '@atlaskit/icon/core/field';
import GrowDiagonalIcon from '@atlaskit/icon/core/grow-diagonal';
import ShrinkDiagonalIcon from '@atlaskit/icon/core/shrink-diagonal';
import TableColumnAddLeftIcon from '@atlaskit/icon/core/table-column-add-left';
import TableColumnAddRightIcon from '@atlaskit/icon/core/table-column-add-right';
import TableRowAddAboveIcon from '@atlaskit/icon/core/table-row-add-above';
import TableRowAddBelowIcon from '@atlaskit/icon/core/table-row-add-below';
import CalculateIcon from '@atlaskit/icon-lab/core/calculate';
import SortOptionsIcon from '@atlaskit/icon-lab/core/sort-options';
import TemplateIcon from '@atlaskit/icon-lab/core/template';
import { token } from '@atlaskit/tokens';
import { isAIFCStagingAreaView } from '@databases/database-state';
import React from 'react';

import { CreateIconComponent } from './CreateIconComponent';

export const EntryOutlineIcon = (props: NewCoreIconProps) => <FieldIcon {...props} />;

export const EntryIconColor = (props: NewCoreIconProps) => (
  <FieldIcon color={token('color.icon.accent.purple')} {...props} />
);

export const PageTemplateIcon = (props: NewCoreIconProps) => <TemplateIcon {...props} />;

export const ExpandIcon = (props: NewCoreIconProps) => <GrowDiagonalIcon {...props} />;

export const HideIcon = (props: NewCoreIconProps) => (
  <EyeOpenStrikethroughIcon {...props} />
);

export const SortIcon = (props: NewCoreIconProps) => <SortOptionsIcon {...props} />;

// if you ever update the HorizontalDragHandlerIconWidth below, please also update the height too
export const HORIZ_DRAG_HANDLER_ICON_WIDTH = 24;
export const HORIZ_DRAG_HANDLER_ICON_HEIGHT = 18;

export const HorizontalDragHandlerIcon = (props: NewCoreIconProps) => (
  <DragHandleHorizontalIcon spacing="spacious" {...props} />
);

export const ActiveHorizontalDragHandlerIcon = (props: NewCoreIconProps) => (
  <DragHandleHorizontalIcon
    spacing="spacious"
    color={token('color.icon.selected')}
    {...props}
  />
);

const isInAIFCStagingAreaView = isAIFCStagingAreaView();

// if you ever update the VerticalDragHandlerIcon below, please also update the height too
export const VERT_DRAG_HANDLER_ICON_HEIGHT = isInAIFCStagingAreaView ? 20 : 25;
export const VERT_DRAG_HANDLER_ICON_WIDTH = isInAIFCStagingAreaView ? 20 : 18;
// Increased width to account for entries checkbox in left gutter
export const DRAG_HANDLER_TD_ELEMENT_WIDTH = 64;

const SmallVerticalDragHandlerIcon = (props: NewCoreIconProps) => (
  <IconButton
    icon={(iconProps) => (
      <DragHandleVerticalIcon {...iconProps} size="small" spacing="none" />
    )}
    label={props.label}
    appearance="subtle"
  />
);

export const VerticalDragHandlerIcon = (props: NewCoreIconProps) =>
  isInAIFCStagingAreaView ? (
    <SmallVerticalDragHandlerIcon {...props} />
  ) : (
    <DragHandleVerticalIcon spacing="spacious" {...props} />
  );

export const ActiveVerticalDragHandlerIcon = (props: NewCoreIconProps) =>
  isInAIFCStagingAreaView ? (
    <SmallVerticalDragHandlerIcon {...props} />
  ) : (
    <DragHandleVerticalIcon color={token('color.icon.selected')} {...props} />
  );

export const InsertRowAboveIcon = (props: NewCoreIconProps) => (
  <TableRowAddAboveIcon {...props} />
);

export const InsertRowBelowIcon = (props: NewCoreIconProps) => (
  <TableRowAddBelowIcon {...props} />
);

export const InsertColumnBeforeIcon = (props: NewCoreIconProps) => (
  <TableColumnAddLeftIcon {...props} />
);

export const InsertColumnAfterIcon = (props: NewCoreIconProps) => (
  <TableColumnAddRightIcon {...props} />
);

export const CollapseIcon = (props: NewCoreIconProps) => (
  <ShrinkDiagonalIcon {...props} />
);

export const SummationIcon = (props: NewCoreIconProps) => <CalculateIcon {...props} />;

export const UpdatedEntryIcon = CreateIconComponent(
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_115_41182)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0 2C0 0.89543 0.895431 0 2 0H22C23.1046 0 24 0.895431 24 2V22C24 23.1046 23.1046 24 22 24H2C0.89543 24 0 23.1046 0 22V2ZM4.5 12C4.5 11.1716 5.17157 10.5 6 10.5H7.5C8.32843 10.5 9 11.1716 9 12C9 12.8284 8.32843 13.5 7.5 13.5H6C5.17157 13.5 4.5 12.8284 4.5 12ZM12 10.5C11.1716 10.5 10.5 11.1716 10.5 12C10.5 12.8284 11.1716 13.5 12 13.5H18C18.8284 13.5 19.5 12.8284 19.5 12C19.5 11.1716 18.8284 10.5 18 10.5H12Z"
        fill="#CD519D"
      />
    </g>
    <defs>
      <clipPath id="clip0_115_41182">
        <rect width="24" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
);
